/* 
🎯 Shell UI Design System v3.0 - Main Entry Point
Purpose: Complete brutalist design system export
Philosophy: Nothing-esque Minimal Brutalist - Function-first, oversized & tactile

Version: 3.0.0 - Complete redesign from space-themed to brutalist aesthetic
Breaking Changes: Complete component and styling overhaul from v2.x
*/

// Import all CSS files - ORDER IS CRITICAL
import './styles/globals.css';        // Design tokens and base styles FIRST
import './styles/theme-dynamic.css';  // Dynamic theme-aware CSS
import './styles/components.css';     // Component-specific styles
import './styles/animations.css';     // Animation definitions
import './styles/background-system.css'; // Minimal background system

// ==========================================
// INTERNAL IMPORTS (for local use)
// ==========================================
import MinimalBackgroundSystemComponent from './components/backgrounds/MinimalBackgroundSystem.jsx';
import { GlassContainer as GlassContainerComponent } from './components/ui/GlassContainer.jsx';

// ==========================================
// BACKGROUND COMPONENTS
// ==========================================
export { default as MinimalBackgroundSystem } from './components/backgrounds/MinimalBackgroundSystem.jsx';
export { GlasmorphicSection, CenteredContent } from './components/backgrounds/MinimalBackgroundSystem.jsx';

// Legacy alias for backward compatibility (deprecated)
export { default as FixedBackgroundSystem } from './components/backgrounds/MinimalBackgroundSystem.jsx';

// ==========================================
// GLASSMORPHIC CONTAINERS
// ==========================================
export { 
  GlassContainer,
  GlassCard,
  MetricCard,
  ShellContainer,
  GlassSection,
  GlassGrid
} from './components/ui/GlassContainer.jsx';

// ==========================================
// BUTTON COMPONENTS
// ==========================================
export { 
  Button,
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  AccentButton,
  ButtonGroup,
  IconButton,
  ButtonLoader
} from './components/ui/Button.jsx';

// ==========================================
// STATUS INDICATORS
// ==========================================
export { 
  StatusIndicator,
  StatusDot,
  OnlineIndicator,
  OfflineIndicator,
  ErrorIndicator,
  LoadingIndicator,
  IdleIndicator,
  StatusGroup,
  StatusBadge
} from './components/ui/StatusIndicator.jsx';

// ==========================================
// FORM COMPONENTS
// ==========================================
export { 
  Toggle,
  ToggleGroup
} from './components/ui/Toggle.jsx';

export { 
  Checkbox,
  CheckboxGroup
} from './components/ui/Checkbox.jsx';

export { 
  TabSwitch
} from './components/ui/TabSwitch.jsx';

export { 
  Input,
  TextInput,
  EmailInput,
  PasswordInput,
  SearchInput
} from './components/ui/Input.jsx';

export { 
  Badge,
  PrimaryBadge,
  SuccessBadge,
  WarningBadge,
  ErrorBadge,
  StatusBadge as BadgeStatus
} from './components/ui/Badge.jsx';

export {
  ProgressBar,
  LoadingProgress
} from './components/ui/ProgressBar.jsx';

export {
  Slider
} from './components/ui/Slider.jsx';

// ==========================================
// DISPLAY COMPONENTS
// ==========================================
export { 
  DotMatrix,
  DotMatrixClock,
  DotMatrixCounter,
  DotMatrixStatus,
  DotMatrixGrid
} from './components/ui/DotMatrix.jsx';

export { 
  Terminal,
  TerminalHeader,
  TerminalContent,
  TerminalLine,
  RetroLoader,
  TerminalCommands
} from './components/ui/Terminal.jsx';

// ==========================================
// SHELL-SPECIFIC COMPONENTS
// ==========================================
export { default as Cursor } from './components/ui/Cursor.jsx';
export { default as UptimeCounter } from './components/ui/UptimeCounter.jsx';
export { default as ShellName } from './components/ui/ShellName.jsx';

// ==========================================
// UTILITY COMPONENTS
// ==========================================
export { 
  Spinner,
  Skeleton,
  LoadingCard,
  CenterSpinner
} from './components/ui/Loading.jsx';

// ==========================================
// LAYOUT COMPONENTS
// ==========================================
export { 
  PageLayout,
  CenteredLayout,
  GridLayout
} from './components/layout/PageLayout.jsx';

// ==========================================
// THEME SYSTEM
// ==========================================
export { 
  ThemeProvider, 
  useTheme, 
  useComponentTheme, 
  withTheme 
} from './components/ThemeProvider.jsx';

export { 
  themeConfig, 
  generateCSSVariables, 
  resolveColor 
} from './theme.config.js';

// ==========================================
// DESIGN SYSTEM METADATA
// ==========================================
export const SHELL_DESIGN_SYSTEM_VERSION = '4.0.0';
export const DESIGN_PHILOSOPHY = 'Terminal-First Design';
export const CORE_PRINCIPLES = [
  'Terminal Aesthetic',
  'Mobile-Friendly (48px targets)',
  'Themeable (7 themes)',
  'ANSI 16-Color Palette'
];

// ==========================================
// THEME UTILITIES
// ==========================================
export const ThemeUtils = {
  // Toggle between light and dark themes
  toggleTheme: () => {
    const currentTheme = document.documentElement.getAttribute('data-theme');
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('shell-theme', newTheme);
    return newTheme;
  },
  
  // Set specific theme
  setTheme: (theme) => {
    if (theme === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
    localStorage.setItem('shell-theme', theme);
  },
  
  // Get current theme
  getCurrentTheme: () => {
    return document.documentElement.getAttribute('data-theme') || 'light';
  },
  
  // Initialize theme from system/storage
  initializeTheme: () => {
    const savedTheme = localStorage.getItem('shell-theme');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const initialTheme = savedTheme || (systemPrefersDark ? 'dark' : 'light');
    
    if (initialTheme === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
    }
    
    return initialTheme;
  }
};

// ==========================================
// ANIMATION UTILITIES
// ==========================================
export const AnimationUtils = {
  // Check if user prefers reduced motion
  prefersReducedMotion: () => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  },
  
  // Respectful animation wrapper
  animate: (element, animation, options = {}) => {
    if (AnimationUtils.prefersReducedMotion()) {
      return Promise.resolve();
    }
    
    return element.animate(animation, {
      duration: 200,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      ...options
    }).finished;
  },
  
  // Tactile feedback animation
  tactileFeedback: (element) => {
    if (AnimationUtils.prefersReducedMotion()) return;
    
    element.style.transform = 'scale(0.98)';
    element.style.transition = 'transform 90ms cubic-bezier(0.16, 1, 0.3, 1)';
    
    setTimeout(() => {
      element.style.transform = '';
    }, 90);
  }
};

// ==========================================
// ACCESSIBILITY UTILITIES
// ==========================================
export const A11yUtils = {
  // Announce to screen readers
  announce: (message, priority = 'polite') => {
    const announcer = document.createElement('div');
    announcer.setAttribute('aria-live', priority);
    announcer.setAttribute('aria-atomic', 'true');
    announcer.className = 'visually-hidden';
    announcer.textContent = message;
    
    document.body.appendChild(announcer);
    
    setTimeout(() => {
      document.body.removeChild(announcer);
    }, 1000);
  },
  
  // Focus management
  focusElement: (element) => {
    if (!element) return;
    
    element.focus();
    
    // Ensure focus is visible
    if (element.scrollIntoView) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  },
  
  // Trap focus within container
  trapFocus: (container) => {
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    const handleTabKey = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    };
    
    container.addEventListener('keydown', handleTabKey);
    firstElement?.focus();
    
    return () => {
      container.removeEventListener('keydown', handleTabKey);
    };
  }
};

// ==========================================
// BREAKPOINT UTILITIES
// ==========================================
export const Breakpoints = {
  mobile: '(max-width: 768px)',
  tablet: '(min-width: 769px) and (max-width: 1024px)',
  desktop: '(min-width: 1025px)',
  
  // Utility functions
  isMobile: () => window.matchMedia(Breakpoints.mobile).matches,
  isTablet: () => window.matchMedia(Breakpoints.tablet).matches,
  isDesktop: () => window.matchMedia(Breakpoints.desktop).matches
};

// ==========================================
// LEGACY COMPATIBILITY
// ==========================================
// Maintain backward compatibility with v2.x exports
// These will be removed in v4.0.0

// Legacy background system (deprecated - use MinimalBackgroundSystem)
export const FixedBackgroundSystem_DEPRECATED = MinimalBackgroundSystemComponent;

// Legacy container naming (deprecated - use GlassContainer)
export const Shell_Container_DEPRECATED = GlassContainerComponent;

console.warn(`
🎯 Shell Design System v3.0.0 Loaded
═══════════════════════════════════════════
Philosophy: Nothing-esque Minimal Brutalist
Principles: Function-first • Oversized & tactile • Monochrome + accent

Breaking changes from v2.x:
• Complete visual redesign from space-themed to brutalist
• New component API and styling system
• Minimal background replaces 11-layer atmospheric system
• Updated color system and design tokens

For migration guide: /docs/design_system_v3/
`);

/*
USAGE EXAMPLES:

// Initialize theme system
import { ThemeUtils } from './shell-design-system';
ThemeUtils.initializeTheme();

// Use core components
import { 
  GlassContainer, 
  Button, 
  StatusIndicator, 
  DotMatrix, 
  Terminal 
} from './shell-design-system';

// Complete shell app
const ShellApp = () => (
  <MinimalBackgroundSystem>
    <GlassContainer variant="large">
      <DotMatrix value="SHELL_READY" size="large" />
      <StatusIndicator status="online" showText={true} />
      <Button variant="primary" onClick={handleAction}>
        Execute Command
      </Button>
      <Terminal 
        lines={[
          { type: 'command', content: 'shell status' },
          { type: 'success', content: 'All systems operational' }
        ]}
      />
    </GlassContainer>
  </MinimalBackgroundSystem>
);

MIGRATION FROM v2.x:
1. Update imports to use new component names
2. Replace FixedBackgroundSystem with MinimalBackgroundSystem
3. Update component props to match new API
4. Review custom styling for new design tokens
5. Test theme switching functionality

PHILOSOPHY ALIGNMENT:
This export structure reflects the brutalist philosophy:
- Clear, functional organization
- No hidden complexity
- Honest about capabilities and limitations
- Utilities grouped by purpose
- Legacy support with clear deprecation path
*/